import moment from 'moment-timezone';
import { ReactElement, useContext } from 'react';
import styled, { useTheme } from 'styled-components';
import { Link } from 'react-router-dom';

import { FullscreenContext } from 'features/fullscreen';

import { Box, ButtonIcon, Flex, Typography } from 'components';
import { createUrl, openCalculator, stripTags } from 'app/utils';

import { StudentPracticeExamAssessmentStatusEnum, ExamsTimeRemaining, useExams } from 'features/exams';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';

export const TitleWrapper = styled(Flex)`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  align-items: center;

  :before {
    content: '';
    display: block;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    background: rgb(255, 255, 255);
    background: linear-gradient(80deg, rgba(255, 255, 255, 0) 0%, ${(props) => props.theme.colors.dark_150} 100%);
  }

  ${({ theme: { mediaQueries } }) => {
    return `
      :before {
        width: 50px;
      }

      ${mediaQueries.desktop} {
        :before {
          width: 70px;
        }
      }
    `;
  }}
`;

const ExamNavbar = (): ReactElement => {
  const { currentTheme, toggleTheme, currentFontsSize, toggleFontSize } = useTheme();
  const { toggleFullscreen, isFullscreen } = useContext(FullscreenContext);

  const { examsDetails } = useExams();

  let timeRemainingSeconds = 0;

  if (examsDetails?.assessmentStatus === StudentPracticeExamAssessmentStatusEnum.StartedNotFinished) {
    if (examsDetails?.assessmentStartDate && !examsDetails?.assessmentFinalizeDate) {
      const endDate = formatDateFromUtcToTimezone(examsDetails?.assessmentEndDate);
      const now = moment();

      timeRemainingSeconds = endDate.diff(now, 'seconds');
    }
  } else if (examsDetails?.assessmentStatus === StudentPracticeExamAssessmentStatusEnum.Finished) {
    timeRemainingSeconds = examsDetails?.durationSeconds;
  } else {
    timeRemainingSeconds = examsDetails?.durationSeconds;
  }

  return (
    <>
      <Flex bg="dark_150" flexWrap="nowrap" borderBottomColor="gray_600" borderBottomStyle="solid" borderBottomWidth="1px" as="header">
        <Flex flexWrap="nowrap">
          <Link to={createUrl('exams/list')}>
            <ButtonIcon
              width={{ m: 60, d: 80 }}
              height={{ m: 60, d: 80 }}
              justifyContent="center"
              icon="close"
              size="large"
              bg="transparent"
              color="white"
              colorHover="primary_400"
              colorClick="primary_200"
              aria-label="Sair"
            />
          </Link>

          <Box position="relative" borderLeftWidth="1px" borderLeftStyle="solid" borderLeftColor="gray_600">
            <TitleWrapper>
              <Flex flexDirection="column" pl={{ m: 20, t: 30 }}>
                <Typography as="h2" type="type1" upper color="white">
                  Simulado
                </Typography>

                <Typography mt={{ m: 1, d: 2 }} as="h1" type="type5" fontWeight="bold" color="white">
                  {stripTags(examsDetails?.name)}
                </Typography>
              </Flex>
            </TitleWrapper>
          </Box>
        </Flex>

        <Flex flexGrow={0} flexWrap="nowrap">
          <Box position="relative" borderLeftWidth="1px" borderLeftStyle="solid" borderLeftColor="gray_600" display={{ m: 'none', d: 'flex' }}>
            <Flex>
              <ButtonIcon
                width={{ m: 60, d: 80 }}
                height={{ m: 60, d: 80 }}
                justifyContent="center"
                icon="calendar"
                size="medium"
                flexShrink={0}
                bg="transparent"
                color="gray_500"
                title={'Data'}
              />

              <Flex justifyContent="center" flexDirection="column" pr={{ m: 20, t: 30 }}>
                <Typography as="h2" type="type0" upper color="gray_500">
                  DATA
                </Typography>
                <Typography mt={{ m: 1, d: 2 }} as="h5" type="type2" fontWeight="bold" color="white">
                  {moment().format('DD[/]MM[/]YYYY')}
                </Typography>
              </Flex>
            </Flex>
          </Box>

          <Flex position="relative" alignItems="center" borderLeftWidth="1px" borderLeftStyle="solid" borderLeftColor="gray_600">
            <Flex>
              <ButtonIcon
                width={{ m: 60, d: 80 }}
                height={{ m: 60, d: 80 }}
                justifyContent="center"
                icon="timer"
                size="medium"
                flexShrink={0}
                bg="transparent"
                color="gray_500"
                title={'Tempo restante'}
                display={{ m: 'none', d: 'flex' }}
              />
              <Flex justifyContent="center" flexDirection="column" pl={{ m: 20, d: 0 }} pr={{ m: 20, t: 30 }}>
                <Typography as="h2" type="type0" upper color="gray_500">
                  TEMPO
                </Typography>
                <Typography mt={{ m: 1, d: 2 }} as="h5" type="type2" fontWeight="bold" color="white">
                  <ExamsTimeRemaining
                    seconds={timeRemainingSeconds}
                    playing={examsDetails?.assessmentStatus === StudentPracticeExamAssessmentStatusEnum.StartedNotFinished}
                  />
                </Typography>
              </Flex>
            </Flex>
          </Flex>

          <ButtonIcon
            width={{ m: 60, d: 80 }}
            height={{ m: 60, d: 80 }}
            justifyContent="center"
            icon={'calculator'}
            onClick={openCalculator}
            size="medium"
            flexShrink={0}
            bg="transparent"
            bgHover="dark_350"
            color="white"
            borderLeftWidth={1}
            borderLeftStyle="solid"
            borderLeftColor="gray_600"
            title={currentTheme === 'light' ? 'Ver tema escuro' : 'Ver tema claro'}
            display={{ m: 'none', d: 'flex' }}
          />

          <ButtonIcon
            width={{ m: 60, d: 80 }}
            height={{ m: 60, d: 80 }}
            justifyContent="center"
            icon={currentTheme === 'light' ? 'themeLight' : 'themeDark'}
            onClick={toggleTheme}
            size="large"
            flexShrink={0}
            bg="transparent"
            bgHover="dark_350"
            color="white"
            borderLeftWidth={1}
            borderLeftStyle="solid"
            borderLeftColor="gray_600"
            title={currentTheme === 'light' ? 'Ver tema escuro' : 'ver tema claro'}
            display={{ m: 'none', d: 'flex' }}
          />

          <ButtonIcon
            width={{ m: 60, d: 80 }}
            height={{ m: 60, d: 80 }}
            justifyContent="center"
            icon={currentFontsSize === 'normal' ? 'fontUp' : 'fontDown'}
            onClick={toggleFontSize}
            size="medium"
            flexShrink={0}
            bg="transparent"
            bgHover="dark_350"
            color="white"
            borderLeftWidth={1}
            borderLeftStyle="solid"
            borderLeftColor="gray_600"
            title={currentFontsSize === 'normal' ? 'Aumentar fonte' : 'Diminuir fonte'}
            display={{ m: 'none', d: 'flex' }}
          />

          <Box flexGrow={0} display={{ m: 'none', d: 'block' }}>
            <ButtonIcon
              width={{ m: 60, d: 80 }}
              height={{ m: 60, d: 80 }}
              justifyContent="center"
              icon={isFullscreen ? 'fullscreenOff' : 'fullscreenOn'}
              onClick={toggleFullscreen}
              size="large"
              flexShrink={0}
              bg="transparent"
              bgHover="dark_350"
              color="white"
              borderLeftWidth={1}
              borderLeftStyle="solid"
              borderLeftColor="gray_600"
              title={isFullscreen ? 'Minimizar' : 'Maximizar'}
            />
          </Box>
        </Flex>
      </Flex>
    </>
  );
};
export default ExamNavbar;
